import React, { useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import { USER_ROLES } from './constants/authConstants';
import Login from './components/auth/Login';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import Dashboard from './components/dashboard/Dashboard';
import UserManagement from './components/auth/UserManagement';
import DepartmentManagement from './components/admin/DepartmentManagement';
import NotificationInbox from './components/notifications/NotificationInbox';
import NotificationHistory from './components/notifications/NotificationHistory';
import PlanningForm from './components/budget/projects/PlanningForm';
import ProjectHistory from './components/budget/projects/ProjectHistory';
import ProjectApproval from './components/budget/projects/ProjectApproval';
import DepartmentSubmissions from './components/budget/projects/DepartmentSubmissions';
import ClearanceProjectWise from './components/state/clearance/ClearanceProjectWise'
import ClearanceDepartmentWise from './components/state/clearance/ClearanceDepartmentWise';
import SessionBudget from './components/budget/session/SessionBudget';
import SessionBudgetDescriptive from './components/budget/session/SessionBudgetDescriptive';
import OngoingProjectForm from './components/budget/ongoing/OngoingProjectForm';
import OngoingProjectDisplay from './components/budget/ongoing/OngoingProjectDisplay';
import StateDepartments from './components/budget/departments/StateDepartments';
import ProjectWiseAllocation from './components/state/allocation/ProjectWiseAllocation';
import DepartmentalAllocation from './components/state/allocation/DepartmentalAllocation';
import DonerForm from './components/central/forms/DonerForm';
import NecForm from './components/central/forms/NecForm';
import DonerInstallmentForm from './components/central/installment/DonerInstallmentForm';
import NecInstallmentForm from './components/central/installment/NecInstallmentForm';
import StateInstallmentForm from './components/central/installment/StateInstallmentForm';
import DonerUcForm from './components/central/uc/DonerUcForm'
import NecUcForm from './components/central/uc/NecUcForm'
import CentralOverallTable from './components/central/overall/CentralOverallTable';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModule, setActiveModule] = useState('dashboard');

  const handleModuleChange = (moduleOrPath) => {
    console.log('App: Module change requested:', moduleOrPath);
    
    if (typeof moduleOrPath === 'string') {
      navigate(moduleOrPath);
      setActiveModule(moduleOrPath.replace('/', '') || 'dashboard');
    } else if (moduleOrPath?.path) {
      navigate(moduleOrPath.path);
      setActiveModule(moduleOrPath.id || moduleOrPath.path.replace('/', '') || 'dashboard');
    } else {
      const modulePath = moduleOrPath?.path || `/${moduleOrPath?.id || 'dashboard'}`;
      navigate(modulePath);
      setActiveModule(moduleOrPath?.id || 'dashboard');
    }
  };

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      
      {/* Protected Routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout activeModule={activeModule} onModuleChange={handleModuleChange} />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Notification Routes */}
        <Route path="notifications" element={<NotificationInbox />} />
        
        {/* Project & Planning Routes */}
        <Route path="budget/projects-planning" element={
          <AdminRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT]}>
            <PlanningForm />
          </AdminRoute>
        } />
        <Route path="budget/projects-history" element={<ProjectHistory />} />
        <Route path="budget/projects-approval" element={
          <AdminRoute allowedRoles={[USER_ROLES.ADMIN]}>
            <ProjectApproval />
          </AdminRoute>
        } />
        
        {/* NEW: Department Submissions Route - Admin & Planning Official Only */}
        <Route path="budget/department-submissions" element={
          <AdminRoute>
            <DepartmentSubmissions />
          </AdminRoute>
        } />

        <Route path="budget/ongoing-projects" element={
          <AdminRoute allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT]}>
            <OngoingProjectForm />
          </AdminRoute>
        } />
        <Route path="budget/ongoing-projects-display" element={
          <AdminRoute>
            <OngoingProjectDisplay />
          </AdminRoute>
        } />
        
        {/* Session Budget Routes */}
        <Route path="budget/session-budget" element={
          <AdminRoute>
            <SessionBudget />
          </AdminRoute>
        } />
        <Route path="budget/session-descriptive" element={
          <AdminRoute>
            <SessionBudgetDescriptive />
          </AdminRoute>
        } />
        <Route path="state/project-wise" element={
          <AdminRoute>
            <ClearanceProjectWise />
          </AdminRoute>
        } />
        <Route path="state/department-wise-allocation" element={
          <AdminRoute>
            <ClearanceDepartmentWise />
          </AdminRoute>
        } />
        
        {/* State Routes */}
        <Route path="budget/departments" element={
          <AdminRoute>
            <StateDepartments />
          </AdminRoute>
        } />
        
        {/* New State Allocation Routes */}
        <Route path="state/project-allocation" element={
          <AdminRoute>
            <ProjectWiseAllocation />
          </AdminRoute>
        } />
        
        <Route path="state/departmental-allocation" element={
          <AdminRoute>
            <DepartmentalAllocation />
          </AdminRoute>
        } />
        
        {/* Central Routes - Admin Only */}
        <Route path="central/doner" element={
          <AdminRoute>
            <DonerForm />
          </AdminRoute>
        } />
        <Route path="central/nec" element={
          <AdminRoute>
            <NecForm />
          </AdminRoute>
        } />
        
        {/* Central Installment Routes - Admin Only */}
        <Route path="central/installment/doner" element={
          <AdminRoute>
            <DonerInstallmentForm />
          </AdminRoute>
        } />
        <Route path="central/installment/nec" element={
          <AdminRoute>
            <NecInstallmentForm />
          </AdminRoute>
        } />
        <Route path="central/installment/state" element={
          <AdminRoute>
            <StateInstallmentForm />
          </AdminRoute>
        } />
        
        {/* Central UC Routes - Admin Only */}
        <Route path="central/uc/doner" element={
          <AdminRoute>
            <DonerUcForm />
          </AdminRoute>
        } />
        <Route path="central/uc/nec" element={
          <AdminRoute>
            <NecUcForm />
          </AdminRoute>
        } />
        <Route 
          path="/central/overall" 
          element={
            <AdminRoute>
              <CentralOverallTable />
            </AdminRoute>
          } 
        />
        
        {/* Admin Routes */}
        <Route path="admin/users" element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        } />
        
        {/* Department Management Route - Admin Only */}
        <Route path="admin/departments" element={
          <AdminRoute>
            <DepartmentManagement />
          </AdminRoute>
        } />
        
        {/* Notification History Route - Admin Only */}
        <Route path="admin/notification-history" element={
          <AdminRoute>
            <NotificationHistory />
          </AdminRoute>
        } />
        
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('StateSync App Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-4">
          <div className="card max-w-md w-full text-center p-8">
            <div className="space-y-6">
              <div className="flex justify-center">
                <div className="bg-error-subtle rounded-full flex items-center justify-center w-16 h-16">
                  <svg className="text-error w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.08 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-xl font-semibold text-text-primary">Something went wrong</h3>
                <p className="text-text-secondary text-sm leading-relaxed">
                  We encountered an unexpected error in StateSync. Please refresh the page or try again later.
                </p>
              </div>

              <div className="bg-surface-secondary rounded-lg p-4">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-6 h-6 bg-navigation rounded-lg flex items-center justify-center">
                    <span className="text-navigation-text font-semibold text-xs">S</span>
                  </div>
                  <span className="text-text-primary font-medium">StateSync</span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="btn-primary w-full"
                >
                  Refresh Page
                </button>
                
                <button
                  onClick={() => window.location.href = '/dashboard'}
                  className="text-sm text-text-tertiary hover:text-text-primary transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>

              {process.env.NODE_ENV === 'development' && (
                <details className="mt-4 text-left">
                  <summary className="text-xs text-text-quaternary cursor-pointer">Error Details</summary>
                  <pre className="text-xs text-error bg-error-subtle rounded overflow-auto mt-2 p-2">
                    {this.state.error?.toString()}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <div className="App bg-bg-secondary min-h-screen">
            <AppContent />
          </div>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;